import { Typography } from 'antd';
import classnames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ReactNode } from 'react';
import { Spinner } from 'src/components/Spinner/Spinner';
import { SpinnerFullWidth } from 'src/components/Spinner/SpinnerFullWidth';
import styled from 'styled-components';

export type BasePageProps = Omit<React.HTMLAttributes<any>, 'title'> & {
    title?: React.ReactNode;
    pageName: string;
    isLoading?: boolean;
    isUpdating?: boolean;
    type?: PageType;
    showOnlyLoading?: boolean;
    headerTopPanel?: ReactNode;
    header?: ReactNode;
};

export const BasePage: React.FC<BasePageProps> = observer(
    ({
        children,
        isLoading,
        title,
        type = PageType.BASE,
        showOnlyLoading,
        headerTopPanel,
        header,
        ...props
    }) => {
        if (showOnlyLoading && isLoading) {
            return (
                <StyledPage type={type} {...props}>
                    <StyledHeaderPanel>{headerTopPanel}</StyledHeaderPanel>
                    {title && <StyledPageTitle>{title}</StyledPageTitle>}
                    {header}
                    <SpinnerFullWidth />
                </StyledPage>
            );
        }

        return (
            <StyledPage type={type} {...props}>
                <StyledHeaderPanel>{headerTopPanel}</StyledHeaderPanel>
                {title && <StyledPageTitle>{title}</StyledPageTitle>}
                {header}
                {isLoading && <StyledSpinner />}
                {children}
            </StyledPage>
        );
    },
);

export enum PageType {
    BASE,
    WIDE_FORM,
    SIMPLE_FORM,
    FULL,
}

type StyledPageProps = { type?: PageType; pageName?: string };

export const StyledPage = styled.div.attrs<StyledPageProps>((props) => ({
    'data-pagename': props.pageName,
}))<StyledPageProps>`
    max-width: ${(props) => getMaxWithByType(props.type)};
    margin: 0 auto;
    position: relative;
    padding: 2rem 1.82rem 2rem 3.7rem;
    .ant-list {
        margin-left: 1.18rem;
    }
`;

const getMaxWithByType = (type?: PageType) => {
    if (type === PageType.WIDE_FORM) {
        return '800px';
    }
    if (type === PageType.SIMPLE_FORM) {
        return '580px';
    }
    if (type === PageType.FULL) {
        return '100%';
    }

    return '90rem';
};

const StyledHeaderPanel = styled.div`
    margin-bottom: 24px;
`;

const StyledPageTitle = styled(Typography.Title)`
    font-size: 40px;
    line-height: 105%;
    color: var(--text-color);

    &.ant-typography {
        margin-bottom: 35px;
    }
`;

const StyledSpinner = styled(Spinner).attrs((props) => ({
    ...props,
    className: classnames(props.className, 'page-spin'),
}))`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    z-index: 999;
    background-color: rgba(255, 255, 255, 0.8);
    opacity: 1;
`;
